import { ComponentType } from '@angular/cdk/portal';
import { Directive, EventEmitter, inject, Input, OnInit, Output, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { DataStateChangeEventArgs, FilterSettingsModel, GridComponent, RecordClickEventArgs, RowDataBoundEventArgs } from '@syncfusion/ej2-angular-grids';
import type * as XLSXTypes from 'xlsx';
import { RequestType } from '../../enums/request-type.enum';
import type { PagedResult, UserRequestDownloadSelection, UserRequestPageRequest, UserRequestTab } from '../models/paged-result';
import { AttachmentData } from '../models/requestDocAttachment';
import { CommonService } from '../services/common.service';
import { DownloadJobService } from '../services/download-job.service';
import { RequestsService } from '../services/requests.service';
import { SnackbarService } from '../services/snackbar.service';
import { historyTabKey, TabFilterStateService } from '../services/tab-filter-state.service';
import { HistoryRequestPath, UserRequestsService } from '../services/user-requests.service';
import { buildPageRequest, DEFAULT_PAGE_SIZE, sortKeysOf, type GridFields } from '../utils/grid-request';
import { answerFilterPopup, isFilterPopupRequest } from '../utils/grid-filter-popup';
import { GridSelection } from '../utils/grid-selection';

/** The members every My Requests / Completed row has, whichever request type the grid lists. */
export interface HistoryRow {
    id: number;
    dateRequested: Date;
    dateAuthorised?: Date | null;
    dateTransaction?: Date | null;
    documentAvailable: boolean;
    documents: { requestId: number }[];
}

/** The most rows one export request asks for; the server allows at most this many. */
const EXPORT_CHUNK_SIZE = 500;

/**
 * Everything the four My Requests / Completed grids (payment, expected funds, transfer, journal) share: the server
 * paging, sorting and filtering, the checkbox filter lists, remembering filters between visits, selecting requests
 * across all pages, the Excel export and the background ZIP downloads. A grid extends this and supplies only what
 * is its own: its columns (in its template), its request type, its fields and its export row.
 */
@Directive()
export abstract class ServerPagedHistoryGrid<TRow extends HistoryRow> implements OnInit {
    @Input() tableHeight: string = '400px';
    @Output() requestCountsUpdated = new EventEmitter<void>();
    @ViewChild('grid') grid!: GridComponent;

    RequestType = RequestType;
    /** The current server page and the total number of rows matching the filters, as the grid binds to them. */
    gridData: { result: TRow[]; count: number } = { result: [], count: 0 };
    loading = true;
    noData = false;
    loadError = false;
    isExporting = false;
    isCompletedTab = false;
    selection = new GridSelection();
    filterSettings: FilterSettingsModel = { type: 'Menu' };
    pageSettings = { pageSize: DEFAULT_PAGE_SIZE, pageSizes: [20, 50, 100] };

    protected readonly userRequestsService = inject(UserRequestsService);
    protected readonly requestService = inject(RequestsService);
    protected readonly commonService = inject(CommonService);
    protected readonly downloadJobService = inject(DownloadJobService);
    protected readonly snackbar = inject(SnackbarService);
    protected readonly tabFilterState = inject(TabFilterStateService);
    protected readonly router = inject(Router);
    protected readonly dialog = inject(MatDialog);

    /** The request type this grid lists. */
    protected abstract readonly requestType: RequestType;
    /** The first part of this type's paged endpoints. */
    protected abstract readonly path: HistoryRequestPath;
    /** The part of the URL that marks the Completed tab (the grid is used by both tabs). */
    protected abstract readonly completedRoute: string;
    /** The type's part of the key filters are remembered under. */
    protected abstract readonly filterKey: Parameters<typeof historyTabKey>[1];
    /** The columns that may be filtered or sorted on the server, and the kind of each. */
    protected abstract readonly fields: GridFields;
    /** The Excel sheet name. */
    protected abstract readonly sheetName: string;
    /** The Excel file name, before the date. */
    protected abstract readonly exportFileName: string;

    /** Identifies the newest page request, so a slower, older response can never overwrite a newer one. */
    private requestSeq = 0;
    private lastFiltersKey = '';

    /** One row of the Excel export. */
    protected abstract exportRow(item: TRow): Record<string, unknown>;

    ngOnInit(): void {
        this.isCompletedTab = this.router.url.includes(this.completedRoute);
        this.filterSettings = this.tabFilterState.getFilterSettings(this.tabKey);

        // First page, with any filters remembered from the last visit applied from the very first request.
        const request = this.buildRequest({ skip: 0, take: this.pageSettings.pageSize, filters: this.filterSettings.columns ?? [] });
        this.lastFiltersKey = JSON.stringify(request.filters);
        this.loadPage(request);
    }

    protected get tab(): UserRequestTab {
        return this.isCompletedTab ? 'Completed' : 'MyRequests';
    }

    private get tabKey(): string {
        return historyTabKey(this.isCompletedTab, this.filterKey);
    }

    /** Reloads the page the grid is showing, for example after a request was withdrawn. */
    protected reload(): void {
        this.selection.clear();
        this.loadPage(this.currentGridRequest());
    }

    /** Retry after a failed load, for the page, sort and filters the grid is currently showing. */
    retryLoad(): void {
        this.loadPage(this.currentGridRequest());
    }

    /** Asks the server for one page; only the newest request's answer is shown. */
    private loadPage(request: UserRequestPageRequest): void {
        const seq = ++this.requestSeq;
        this.loading = true;
        this.loadError = false;

        this.userRequestsService.searchRequests<TRow>(this.path, request)
            .then(res => { if (seq === this.requestSeq) this.showPage(res); })
            .catch(() => { if (seq === this.requestSeq) this.showPageError(); });
    }

    private showPage(res: PagedResult<TRow>): void {
        const rows = res.items ?? [];
        rows.forEach(row => this.convertDatesToLocal(row));
        this.loading = false;
        this.noData = res.totalCount === 0;
        // A new object, not a changed one: the grid is waiting for it to finish the paging/sorting/filtering it asked for.
        this.gridData = { result: rows, count: res.totalCount };

        // The server moves a page past the end back to the last page; keep the pager on the page it actually shows.
        if (this.grid && this.grid.pageSettings.currentPage !== res.page) {
            this.grid.pageSettings.currentPage = res.page;
        }
    }

    private showPageError(): void {
        this.loading = false;
        this.loadError = true;
        // Hand the grid back what it already had so it stops waiting; the previous page stays visible.
        this.gridData = { result: this.gridData.result, count: this.gridData.count };
        this.snackbar.errorSnackBar('Unable to load the requests. Please try again.');
    }

    private convertDatesToLocal(row: TRow): void {
        if (row.dateRequested) row.dateRequested = this.commonService.convertUtcToLocal(row.dateRequested);
        if (row.dateAuthorised) row.dateAuthorised = this.commonService.convertUtcToLocal(row.dateAuthorised);
        if (row.dateTransaction) row.dateTransaction = this.commonService.convertUtcToLocal(row.dateTransaction);
    }

    private buildRequest(state: Parameters<typeof buildPageRequest>[1]): UserRequestPageRequest {
        return { tab: this.tab, ...buildPageRequest(this.fields, state) };
    }

    /** The request for the page, sort and filters the grid is showing right now. */
    private currentGridRequest(): UserRequestPageRequest {
        const pageSize = this.grid?.pageSettings.pageSize ?? this.pageSettings.pageSize;
        const currentPage = this.grid?.pageSettings.currentPage ?? 1;
        return this.buildRequest({
            skip: (currentPage - 1) * pageSize,
            take: pageSize,
            sorted: sortKeysOf(this.grid),
            filters: this.grid?.filterSettings.columns ?? this.filterSettings.columns ?? []
        });
    }

    /** The grid asks for data: a page, sort or filter change, or the contents of a checkbox filter. */
    onDataStateChange(state: DataStateChangeEventArgs): void {
        if (isFilterPopupRequest(state)) {
            answerFilterPopup(state, field => this.userRequestsService.getFilterValues(this.path, this.tab, field));
            return;
        }

        const request = this.buildRequest({
            skip: state.skip,
            take: state.take,
            // The state argument lists the sort keys lowest priority first, so they are read from the grid instead.
            sorted: sortKeysOf(this.grid),
            // The grid has already added the new filter to its settings when it asks for the data.
            filters: this.grid?.filterSettings.columns ?? []
        });

        // Remember the filters now: in this server-driven mode the grid does not raise actionComplete once the data arrives.
        this.rememberFilters();

        // Rows selected under one set of filters must not stay selected, unseen, under another.
        const filtersKey = JSON.stringify(request.filters);
        if (filtersKey !== this.lastFiltersKey) {
            this.lastFiltersKey = filtersKey;
            this.selection.clear();
        }

        this.loadPage(request);
    }

    /**
     * Keeps the grid's filters for when the tab is opened again. The grid's own filter objects hide their values behind
     * accessors, so a plain copy of them comes out empty; the values are read out one by one instead.
     */
    private rememberFilters(): void {
        const filters = (this.grid?.filterSettings.columns ?? []).map(column => ({
            field: column.field,
            operator: column.operator,
            value: column.value,
            predicate: column.predicate,
            matchCase: column.matchCase,
            ignoreAccent: column.ignoreAccent
        }));
        this.tabFilterState.set(this.tabKey, filters);
    }

    resetFilters(): void {
        const hadFilters = (this.grid?.filterSettings.columns?.length ?? 0) > 0;
        this.selection.clear();
        this.tabFilterState.clearFiltering(this.tabKey, this.grid);
        // Clearing filters makes the grid ask for the data itself; with no filter to clear it asks for nothing, so reload here.
        if (!hadFilters) {
            this.loadPage(this.currentGridRequest());
        }
    }

    // ---- Selection: across every page ----

    /** The number of requests matching the filters, across every page. */
    get totalCount(): number {
        return this.gridData.count;
    }

    /** The number of requests selected, across every page. */
    get selectedCount(): number {
        return this.selection.count(this.gridData.count);
    }

    isSelected(row: TRow): boolean {
        return this.selection.isSelected(String(row.id));
    }

    toggleSelection(row: TRow): void {
        this.selection.toggle(String(row.id));
    }

    /** True when every row on this page is selected. */
    get allSelected(): boolean {
        const rows = this.gridData.result;
        return rows.length > 0 && rows.every(row => this.isSelected(row));
    }

    get someSelected(): boolean {
        return !this.allSelected && this.gridData.result.some(row => this.isSelected(row));
    }

    /** The header checkbox: ticking it selects every matching request on every page; unticking clears the selection. */
    toggleSelectAll(checked: boolean): void {
        if (checked) {
            this.selection.selectAllMatching();
        } else {
            this.selection.clear();
        }
        this.applySelectionClassToVisibleRows();
    }

    rowDataBound(args: RowDataBoundEventArgs): void {
        (args.row as HTMLElement)?.classList.toggle('selected-row', this.isSelected(args.data as TRow));
    }

    onRecordClick(args: RecordClickEventArgs): void {
        const target = args.target as HTMLElement;
        if (target.closest('button')) return;

        const data = args.rowData as TRow;
        this.toggleSelection(data);
        this.highlightRow(data);
    }

    onCheckboxClick(event: MouseEvent, data: TRow): void {
        event.stopPropagation();
        this.toggleSelection(data);
        this.highlightRow(data);
    }

    private highlightRow(data: TRow): void {
        if (!this.grid) return;
        const records = this.grid.getCurrentViewRecords() as TRow[];
        const index = records.findIndex(row => row.id === data.id);
        if (index === -1) return;
        (this.grid.getRowByIndex(index) as HTMLElement)?.classList.toggle('selected-row', this.isSelected(data));
    }

    private applySelectionClassToVisibleRows(): void {
        if (!this.grid) return;
        const records = this.grid.getCurrentViewRecords() as TRow[];
        const rows = this.grid.getRows();
        records.forEach((row, index) => {
            (rows[index] as HTMLElement)?.classList.toggle('selected-row', this.isSelected(row));
        });
    }

    // ---- Downloads ----

    /** True while a ZIP is being started or prepared; a user may have only one at a time. */
    get downloadBusy(): boolean {
        return this.downloadJobService.inProgress;
    }

    /** Downloads a ZIP of the PDFs of every request matching the current filters, however many there are. */
    downloadAllAsPdf(): void {
        this.startDownload({ allMatching: true, ids: [], excludedIds: [] });
    }

    /** Downloads a ZIP of the PDFs of the selected requests, wherever in the list they are. */
    downloadSelectedAsPdf(): void {
        if (this.selectedCount === 0) return;

        const allMatching = this.selection.allMatching;
        const ids = this.selection.tickedKeys.map(Number);
        const excludedIds = this.selection.excludedKeys.map(Number);
        this.startDownload({ allMatching, ids: allMatching ? [] : ids, excludedIds: allMatching ? excludedIds : [] });
    }

    /** Asks the server to prepare the ZIP in the background; the finished ZIP is saved by the DownloadJobService. */
    private startDownload(choice: Pick<UserRequestDownloadSelection, 'allMatching' | 'ids' | 'excludedIds'>): void {
        const selection: UserRequestDownloadSelection = {
            requestType: this.requestType,
            tab: this.tab,
            filters: choice.allMatching ? this.currentGridRequest().filters : [],
            ...choice
        };
        void this.downloadJobService.begin(() => this.userRequestsService.startDownload(selection));
    }

    // ---- Excel export ----

    /** Exports every request matching the current filters, in the grid's sort order, fetched chunk by chunk. */
    async exportDataToExcel(): Promise<void> {
        if (this.isExporting || this.gridData.count === 0) return;

        this.isExporting = true;
        try {
            const { sort, filters } = this.currentGridRequest();
            const rows: Record<string, unknown>[] = [];
            for (let skip = 0; ; skip += EXPORT_CHUNK_SIZE) {
                const chunk = await this.userRequestsService.getExportRows<TRow>(this.path, { tab: this.tab, skip, take: EXPORT_CHUNK_SIZE, sort, filters });
                chunk.items.forEach(item => {
                    this.convertDatesToLocal(item);
                    rows.push(this.exportRow(item));
                });
                if (chunk.items.length === 0 || skip + EXPORT_CHUNK_SIZE >= chunk.totalCount) break;
            }

            if (rows.length === 0) return;
            await this.writeWorkbook(rows);
        } catch (err) {
            console.error('Export failed', err);
            this.snackbar.errorSnackBar('The export could not be created. Please try again.');
        } finally {
            this.isExporting = false;
        }
    }

    private async writeWorkbook(rows: Record<string, unknown>[]): Promise<void> {
        const XLSX = await import('xlsx');
        const ws: XLSXTypes.WorkSheet = XLSX.utils.json_to_sheet(rows);
        const wb: XLSXTypes.WorkBook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, this.sheetName);

        const reportDate = new Date().toLocaleDateString('en-GB', {
            year: 'numeric', month: '2-digit', day: '2-digit'
        }).replace(/\//g, '-');
        XLSX.writeFile(wb, `${this.exportFileName} - ${reportDate}.xlsx`);
    }

    /** Formats a date the way the export shows it. */
    protected exportDate(value: Date | null | undefined): string {
        return value ? new Date(value).toLocaleDateString() : '';
    }

    // ---- Row actions ----

    /** Opens the request's details dialog; when it was changed there (a withdrawal), the page and the counts are refreshed. */
    protected openDetails(component: ComponentType<unknown>, row: TRow, height?: string): void {
        const dialogRef = this.dialog.open(component, {
            width: '1000px',
            ...(height ? { height } : {}),
            data: row,
            disableClose: true
        });
        dialogRef.afterClosed().subscribe(result => {
            if (result) {
                this.reload();
                this.requestCountsUpdated.emit();
            }
        });
    }

    downloadDocuments(element: TRow): void {
        if (!element.documentAvailable || !element.documents?.length) {
            console.error('No documents available for this request');
            return;
        }

        this.requestService.getRequestDocumentList(element.documents[0].requestId, this.requestType).then(res => {
            this.downloadMultipleBase64Files(res.attachments ?? []);
        });
    }

    downloadMultipleBase64Files(files: AttachmentData[]): void {
        files.forEach(file => {
            const { attachments, fileName } = file;
            const byteCharacters = atob(attachments);
            const byteNumbers = new Array(byteCharacters.length);
            for (let i = 0; i < byteCharacters.length; i++) {
                byteNumbers[i] = byteCharacters.charCodeAt(i);
            }
            const byteArray = new Uint8Array(byteNumbers);
            const blob = new Blob([byteArray], { type: 'application/octet-stream' });
            const link = document.createElement('a');
            link.href = URL.createObjectURL(blob);
            link.download = fileName;
            link.click();
        });
    }

    downloadRequestPdf(id: number, requestType: RequestType): void {
        this.requestService.downloadRequestPdf(id, requestType);
    }
}
